import React, { useCallback } from 'react';
import { Box, Inline, Flex, xcss } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import { useIntl } from '@atlassian/jira-intl';
import { RelativeTime } from '../../../../../common/ui/relative-time/index.tsx';
import { UserAvatar } from '../../../../../common/ui/user-avatar/index.tsx';
import {
	ActivityTypes,
	BoundaryIds,
	SectionIds,
	type Activity,
} from '../../../../../common/types.tsx';
import { ActivityMessage } from '../../../../../common/ui/activity-message/index.tsx';

import { useSectionAnalytics } from '../../../../../common/ui/analytics/index.tsx';
import messages from './messages.tsx';

type PriorityIconProps = {
	name: string;
	iconUrl: string;
};

const PriorityIcon = ({ name, iconUrl }: PriorityIconProps) => {
	return (
		<Inline alignBlock="center" alignInline="center" xcss={priorityIconWrapperStyles}>
			<Image src={iconUrl} alt="" height={16} width={16} />
			{name}
		</Inline>
	);
};

export type PriorityChangedProps = {
	activity: Activity<'PRIORITY_CHANGED'>;
};

export const PriorityChanged = ({
	activity: {
		created,
		issueData,
		author,
		data: {
			fromName: oldPriority,
			toName: newPriority,
			fromIconUrl: oldPriorityIcon,
			toIconUrl: newPriorityIcon,
		},
	},
}: PriorityChangedProps) => {
	const { formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.RecentActivity,
		BoundaryIds.RecentActivity,
	);

	const onActivityItemClick = useCallback(
		(target: 'user' | 'issue') => {
			fireSectionAnalytics(target, {
				boundaryId: BoundaryIds.RecentActivity,
				activityType: ActivityTypes.PRIORITY_CHANGED,
			});
		},
		[fireSectionAnalytics],
	);

	return (
		<Box>
			<Flex columnGap="space.100">
				<UserAvatar author={author} />
				<Flex direction="column" rowGap="space.050">
					<ActivityMessage author={author} issueData={issueData} onClick={onActivityItemClick}>
						{({ author: activityAuthor, issueLink }) => (
							<Box xcss={inlineStyles}>
								{formatMessage(messages.priorityChangedContent, {
									author: activityAuthor,
									issueLink,
									oldPriority: <PriorityIcon name={oldPriority} iconUrl={oldPriorityIcon} />,
									newPriority: <PriorityIcon name={newPriority} iconUrl={newPriorityIcon} />,
								})}
							</Box>
						)}
					</ActivityMessage>
					<RelativeTime time={created} />
				</Flex>
			</Flex>
		</Box>
	);
};

const priorityIconWrapperStyles = xcss({
	display: 'inline-flex',
	verticalAlign: 'text-top',
});

const inlineStyles = xcss({
	display: 'inline',
});
